import { MIN_PHONE_DIGITS } from 'lib/contactSearch'
import { ES_SPECIAL_CHARS } from 'lib/esSpecialChars'
import { phoneDigits } from 'lib/phone'

/** Fewest non-space characters before a text query fires a request. */
export const MIN_QUERY_CHARS = 2

/** Digits plus the punctuation people paste around a phone number. */
const NUMBER_SHAPED = /^[\d\s()+.-]+$/

export type PaletteQuery = {
  text: string
  words: string[]
  /** Normalised phone digits, or empty when there are too few to match on. */
  digits: string
  isNumberShaped: boolean
  /** The whole word carrying an `@`, or empty when the input holds none. */
  emailWord: string
}

export function parsePaletteQuery(raw: string): PaletteQuery {
  const text = raw.trim()
  const digits = phoneDigits(text)
  const words = text.split(/\s+/).filter(Boolean)
  return {
    text,
    words,
    digits: digits.length >= MIN_PHONE_DIGITS ? digits : ``,
    isNumberShaped: text.length > 0 && NUMBER_SHAPED.test(text),
    emailWord: words.find((word) => word.includes(`@`)) ?? ``,
  }
}

export function isSearchable(query: PaletteQuery): boolean {
  if (query.isNumberShaped) return query.digits.length >= MIN_PHONE_DIGITS
  return query.text.replace(/\s/g, ``).length >= MIN_QUERY_CHARS
}

const wildcard = (field: string, value: string): string => `${field}:*${value.replace(ES_SPECIAL_CHARS, `\\$&`)}*`

/**
 * The `email` index field is tokenized on `@`, so a single wildcard spanning the `@`
 * matches nothing while each side on its own matches. Emit one wildcard per non-empty
 * side of the split, ANDed, and only for the word that carries the `@`: the rest of the
 * input is a name, not part of the address.
 */
function emailClause(query: PaletteQuery): string {
  const sides = query.emailWord.split(`@`).filter(Boolean)
  if (sides.length === 0) return ``
  if (sides.length === 1) return wildcard(`email`, sides[0])
  return `(${sides.map((side) => wildcard(`email`, side)).join(` AND `)})`
}

/**
 * Contacts `search` string. Words are ANDed on name; a phone run or an email is ORed
 * on so "smith 4670" finds the Smith whose number ends 4670. A pure number skips the
 * name clause. The phone clause repeats the digits, which keeps the request's total
 * digit count away from exactly ten, the shape the API collapses to a bare phone search
 * (TRK-5304).
 */
export function contactsSearchTerm(query: PaletteQuery): string {
  const clauses: string[] = []
  if (!query.isNumberShaped && query.words.length > 0) {
    clauses.push(`(${query.words.map((word) => wildcard(`name`, word)).join(` AND `)})`)
  }
  if (query.digits) {
    const d = query.digits
    clauses.push(`(cellPhone:*${d}* OR homePhone:*${d}* OR workPhone:*${d}*)`)
  }
  const email = emailClause(query)
  if (email) clauses.push(email)
  return clauses.join(` OR `)
}

/** Activities `search` string: the page's default exclusion ANDed with the same matching rules. */
export function activitiesSearchTerm(query: PaletteQuery, exclusion: string): string {
  const clauses: string[] = []
  if (!query.isNumberShaped && query.words.length > 0) {
    const perWord = query.words.map(
      (word) => `(${wildcard(`contactName`, word)} OR ${wildcard(`nameFromActivity`, word)})`,
    )
    clauses.push(perWord.join(` AND `))
  }
  if (query.digits) {
    const d = query.digits
    clauses.push(`(contactPhone:*${d}* OR phoneFromActivity:*${d}* OR contacts.phone:*${d}* OR trackingNumber:*${d}*)`)
  }
  const email = emailClause(query)
  if (email) clauses.push(email)
  return `${exclusion} AND (${clauses.join(` OR `)})`
}
